import {
  CareCircleExtractedFactsResponse,
  ExtractedFactsResponse,
  FactRef,
  IntakeSummary,
  LiveTranscriptionTicket,
  PreviewedFact,
  PromptCoverage,
} from '@karehero/models';

import api, { apiV1Url } from '.';

/** The subprotocol the live transcription socket speaks; the ticket is offered after it. */
export const LIVE_TRANSCRIPTION_PROTOCOL = 'kh-live-transcription';

/**
 * Where a ticket opens the live transcription socket (ws for http, wss for
 * https), and the subprotocols that carry the ticket.
 */
export const liveTranscriptionSocket = (ticket: string) => {
  const url = new URL('live-transcription', apiV1Url);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return { url: url.href, protocols: [LIVE_TRANSCRIPTION_PROTOCOL, ticket] };
};

interface GetExtractedFactsParams {
  careCircleId: string;
  documentId: string;
}

interface GetCareCircleExtractedFactsParams {
  careCircleId: string;
  /** Only the facts answering any of these care profile questions. Omit for all. */
  questionIds?: string[];
}

/** Repeats the parameter per question, as the API reads it. */
const questionIdsQuery = (questionIds?: string[]) =>
  questionIds?.length
    ? `?${questionIds.map((id) => `questionIds=${encodeURIComponent(id)}`).join('&')}`
    : '';

const extractedFactsUrl = ({
  careCircleId,
  documentId,
}: GetExtractedFactsParams) =>
  `care-circle/${careCircleId}/organise-document/${documentId}/extracted-facts`;

interface FactActionParams {
  careCircleId: string;
  factId: string;
}

interface IntakeSummaryParams {
  careCircleId: string;
}

interface ConfirmIntakeSummaryParams extends IntakeSummaryParams {
  summaryId: string;
}

interface LiveTranscriptionParams {
  careCircleId: string;
}

interface SubmitTextCaptureParams {
  careCircleId: string;
  body: {
    text: string;
    messageId: string;
    context?: string;
    source?: string;
    /** `voice` for a voice note's transcript. */
    messageKind?: 'text' | 'voice';
  };
}

interface CheckPromptCoverageParams {
  careCircleId: string;
  body: {
    prompts: string[];
    /** Everything said so far, typed or spoken. */
    said: string;
  };
}

interface PreviewFactsParams {
  careCircleId: string;
  body: {
    /** What has been typed or said but not yet sent. */
    said: string;
    context?: string;
    source?: string;
    facts: FactRef[];
  };
}

interface FactPreviewResponse {
  /** One per fact, in the order asked. */
  facts: PreviewedFact[];
}

interface PromptCoverageResponse {
  /** One per prompt, in the order given. */
  coverage: PromptCoverage[];
  /** The index of the prompt their last words are on; absent for none. */
  stillOn?: number;
}

interface TextCaptureResponse {
  extractionId: string;
}

export const inferenceApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getDocumentExtractedFacts: builder.query<
      ExtractedFactsResponse,
      GetExtractedFactsParams
    >({
      query: (params) => ({ url: extractedFactsUrl(params) }),
      providesTags: (_result, _error, { careCircleId }) => [
        { type: 'InferenceExtraction', id: careCircleId },
      ],
    }),
    getCareCircleExtractedFacts: builder.query<
      CareCircleExtractedFactsResponse,
      GetCareCircleExtractedFactsParams
    >({
      query: ({ careCircleId, questionIds }) => ({
        url: `care-circle/${careCircleId}/extracted-facts${questionIdsQuery(questionIds)}`,
      }),
      providesTags: (_result, _error, { careCircleId }) => [
        { type: 'InferenceExtraction', id: careCircleId },
      ],
    }),
    confirmExtractedFact: builder.mutation<void, FactActionParams>({
      query: ({ careCircleId, factId }) => ({
        url: `care-circle/${careCircleId}/extracted-facts/${factId}/confirm`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { careCircleId }) => [
        { type: 'InferenceExtraction', id: careCircleId },
        'CareProfileCategories',
        'CareProfileAnswers',
      ],
    }),
    dismissExtractedFact: builder.mutation<void, FactActionParams>({
      query: ({ careCircleId, factId }) => ({
        url: `care-circle/${careCircleId}/extracted-facts/${factId}/dismiss`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { careCircleId }) => [
        { type: 'InferenceExtraction', id: careCircleId },
      ],
    }),
    requestIntakeSummary: builder.mutation<IntakeSummary, IntakeSummaryParams>({
      query: ({ careCircleId }) => ({
        url: `care-circle/${careCircleId}/intake-summary`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { careCircleId }) => [
        { type: 'IntakeSummary', id: careCircleId },
      ],
    }),
    getIntakeSummary: builder.query<IntakeSummary | null, IntakeSummaryParams>({
      queryFn: async (
        { careCircleId },
        _queryApi,
        _extraOptions,
        baseQuery,
      ) => {
        const result = await baseQuery({
          url: `care-circle/${careCircleId}/intake-summary`,
        });
        if (result.error?.status === 404) return { data: null };
        if (result.error) return { error: result.error };
        return { data: result.data as IntakeSummary };
      },
      providesTags: (_result, _error, { careCircleId }) => [
        { type: 'IntakeSummary', id: careCircleId },
      ],
    }),
    confirmIntakeSummary: builder.mutation<void, ConfirmIntakeSummaryParams>({
      query: ({ careCircleId, summaryId }) => ({
        url: `care-circle/${careCircleId}/intake-summary/${summaryId}/confirm`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { careCircleId }) => [
        { type: 'IntakeSummary', id: careCircleId },
      ],
    }),
    createLiveTranscriptionTicket: builder.mutation<
      LiveTranscriptionTicket,
      LiveTranscriptionParams
    >({
      query: ({ careCircleId }) => ({
        url: `care-circle/${careCircleId}/live-transcription-ticket`,
        method: 'POST',
      }),
    }),
    checkPromptCoverage: builder.mutation<
      PromptCoverageResponse,
      CheckPromptCoverageParams
    >({
      query: ({ careCircleId, body }) => ({
        url: `care-circle/${careCircleId}/prompt-coverage`,
        method: 'POST',
        body,
      }),
    }),
    previewFacts: builder.mutation<FactPreviewResponse, PreviewFactsParams>({
      query: ({ careCircleId, body }) => ({
        url: `care-circle/${careCircleId}/fact-preview`,
        method: 'POST',
        body,
      }),
    }),
    submitTextCapture: builder.mutation<
      TextCaptureResponse,
      SubmitTextCaptureParams
    >({
      query: ({ careCircleId, body }) => ({
        url: `care-circle/${careCircleId}/text-capture`,
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useGetDocumentExtractedFactsQuery,
  useGetCareCircleExtractedFactsQuery,
  useLazyGetCareCircleExtractedFactsQuery,
  useLazyGetDocumentExtractedFactsQuery,
  useConfirmExtractedFactMutation,
  useDismissExtractedFactMutation,
  useRequestIntakeSummaryMutation,
  useGetIntakeSummaryQuery,
  useConfirmIntakeSummaryMutation,
  useSubmitTextCaptureMutation,
  useCreateLiveTranscriptionTicketMutation,
  useCheckPromptCoverageMutation,
  usePreviewFactsMutation,
} = inferenceApi;

export default inferenceApi;
